package com.sddamico.components.widgets

import androidx.compose.runtime.Composable
import com.sddamico.SitePalette
import com.sddamico.components.motion.Durations
import com.sddamico.components.motion.Ease
import com.sddamico.components.motion.FadeInKeyframes
import com.sddamico.components.motion.HoldPageTransition
import com.sddamico.components.motion.ReducedMotionQuery
import com.sddamico.components.motion.PressableStyle
import com.sddamico.components.motion.rememberContinuesLoading
import com.varabyte.kobweb.compose.css.Animation
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.after
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

/** Shimmering stand-in for content that is still loading. */
val SkeletonStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(SitePalette.surface)
            .borderRadius(8.px)
            .position(Position.Relative)
            .overflow(Overflow.Hidden)
    }
    after {
        Modifier
            .content("")
            .position(Position.Absolute)
            .styleModifier { property("inset", "0") }
            .styleModifier {
                property("background", "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.04), transparent)")
            }
            .animation(
                ShimmerKeyframes.toAnimation(
                    colorMode, 1400.ms, AnimationTimingFunction.Linear, iterationCount = AnimationIterationCount.Infinite
                )
            )
    }
    cssRule(ReducedMotionQuery, "::after") { Modifier.display(DisplayStyle.None) }
}

val ShimmerKeyframes = Keyframes {
    from { Modifier.translateX((-100).percent) }
    to { Modifier.translateX(100.percent) }
}

@Composable
fun SkeletonBlock(height: CSSLengthOrPercentageNumericValue, width: CSSLengthOrPercentageNumericValue = 100.percent) {
    Div(SkeletonStyle.toModifier().height(height).width(width).toAttrs())
}

/**
 * Placeholder for a page whose data hasn't arrived. It also holds the page transition, so in the common case the
 * previous page stays up until the data lands and this never shows; it appears only for slow loads.
 */
@Composable
fun PageLoading() {
    val continues = rememberContinuesLoading()
    HoldPageTransition()
    Column(
        Modifier
            .fillMaxWidth()
            .gap(1.cssRem)
            .then(
                if (continues) {
                    Modifier.animation(Animation.None).attrsModifier { attr("data-motion-continue", "") }
                } else {
                    // Fast first loads finish before this shows, which avoids a one-frame flash of grey.
                    Modifier.animation(
                        FadeInKeyframes.toAnimation(
                            Durations.ENTER.ms, Ease.Out.fn, delay = 150.ms, fillMode = AnimationFillMode.Backwards
                        )
                    )
                }
            )
    ) {
        Column(Modifier.fillMaxWidth().gap(0.5.cssRem)) {
            SkeletonBlock(2.25.cssRem, 14.cssRem)
            SkeletonBlock(0.875.cssRem, 8.cssRem)
        }
        SkeletonBlock(7.cssRem)
        SkeletonBlock(5.cssRem)
        SkeletonBlock(5.cssRem)
    }
}

@Composable
fun ErrorCard(message: String, onRetry: (() -> Unit)? = null) {
    Column(
        Modifier
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderRadius(8.px)
            .padding(2.cssRem)
            .fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        SpanText(
            "Something went wrong",
            Modifier.fontSize(1.125.cssRem).fontWeight(FontWeight.SemiBold).color(SitePalette.text)
        )
        SpanText(
            message,
            Modifier.fontSize(0.875.cssRem).color(SitePalette.textSecondary).margin(top = 0.5.cssRem)
        )
        if (onRetry != null) {
            Div(
                PressableStyle.toModifier()
                    .margin(top = 1.cssRem)
                    .backgroundColor(SitePalette.primary)
                    .color(Colors.White)
                    .borderRadius(8.px)
                    .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
                    .fontSize(0.875.cssRem)
                    .fontWeight(FontWeight.SemiBold)
                    .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
                    .onClick { onRetry() }
                    .toAttrs()
            ) {
                Text("Retry")
            }
        }
    }
}

@Composable
fun EmptyState(message: String) {
    Box(
        Modifier
            .backgroundColor(SitePalette.surface)
            .border(1.px, LineStyle.Solid, SitePalette.border)
            .borderRadius(8.px)
            .padding(2.cssRem)
            .fillMaxWidth(),
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            message,
            Modifier.color(SitePalette.textSecondary).fontSize(0.9375.cssRem)
        )
    }
}
